import type { ActionChoice } from '@/components/layout/chat-pane.types'
import type { DocumentReference } from '@/components/layout/chat-pane.types'
import { isTrustedDocumentRef } from '@/lib/chat-markdown-links'

const OPEN_LABEL_RE = /^open\b/i

/** True when the choice should navigate to a document, not send a chat message. */
export function isOpenDocumentActionChoice(choice: ActionChoice): boolean {
  if (choice.action === 'open_document') return true
  if (choice.action === 'send_message' || choice.action === 'fill_comment_form') return false
  if (choice.documentId || choice.filePath) return true
  return OPEN_LABEL_RE.test(choice.label?.trim() ?? '')
}

function titleMatchesLabel(label: string, title: string): boolean {
  const l = label.toLowerCase()
  const t = title.toLowerCase().trim()
  if (!t) return false
  if (l.includes(t)) return true
  // Partial match on first 24 chars (handles truncated titles in labels)
  const head = t.slice(0, 24)
  if (head.length >= 8 && l.includes(head)) return true
  const words = t.split(/[^a-z0-9]+/).filter((w) => w.length > 3)
  const overlap = words.filter((w) => l.includes(w)).length
  return overlap >= 2
}

function trustedDocumentEntries(
  documentsMap?: Map<string, DocumentReference>,
): Array<[string, DocumentReference]> {
  if (!documentsMap?.size) return []
  const entries: Array<[string, DocumentReference]> = []
  for (const [id, doc] of documentsMap.entries()) {
    if (doc.fromMarkdown && !doc.path) continue
    entries.push([id, doc])
  }
  return entries
}

/**
 * Attach documentId/filePath to "Open ..." choices when the assistant already loaded docs this turn.
 */
export function enrichActionChoices(
  choices: ActionChoice[],
  documentsMap?: Map<string, DocumentReference>,
): ActionChoice[] {
  if (!choices?.length) return choices
  return choices.map((choice) => {
    if (choice.action === 'send_message') return choice
    if (isTrustedDocumentRef(choice.documentId, documentsMap)) return choice
    if (choice.filePath && !choice.documentId) return choice
    if (
      choice.action !== 'open_document' &&
      !OPEN_LABEL_RE.test(choice.label?.trim() ?? '')
    ) {
      return choice
    }

    const trusted = trustedDocumentEntries(documentsMap)
    for (const [id, doc] of trusted) {
      const title = doc.title ?? ''
      if (title && titleMatchesLabel(choice.label, title)) {
        return {
          ...choice,
          action: 'open_document',
          documentId: id,
          filePath: doc.path,
        }
      }
    }
    if (trusted.length === 1) {
      const [id, doc] = trusted[0]
      return {
        ...choice,
        action: 'open_document',
        documentId: id,
        filePath: doc.path,
      }
    }
    return choice
  })
}

export type ResolvedActionChoice =
  | { type: 'open_document'; documentId?: string; filePath?: string }
  | { type: 'send_message'; text: string }
  | { type: 'fill_comment_form'; text: string }

export function resolveActionChoiceTarget(
  choice: ActionChoice,
  documentsMap?: Map<string, DocumentReference>,
): ResolvedActionChoice {
  if (choice.action === 'fill_comment_form') {
    return { type: 'fill_comment_form', text: choice.message ?? choice.label }
  }

  if (choice.action === 'send_message') {
    return { type: 'send_message', text: choice.message ?? choice.label }
  }

  if (choice.documentId || choice.filePath) {
    if (
      choice.documentId &&
      documentsMap?.size &&
      !isTrustedDocumentRef(choice.documentId, documentsMap)
    ) {
      const remapped = enrichActionChoices(
        [{ ...choice, documentId: undefined, filePath: undefined }],
        documentsMap,
      )[0]
      if (remapped.documentId || remapped.filePath) {
        return {
          type: 'open_document',
          documentId: remapped.documentId,
          filePath: remapped.filePath,
        }
      }
    }
    return {
      type: 'open_document',
      documentId: choice.documentId,
      filePath: choice.filePath,
    }
  }

  const enriched = enrichActionChoices([choice], documentsMap)[0]
  if (enriched.documentId || enriched.filePath) {
    return {
      type: 'open_document',
      documentId: enriched.documentId,
      filePath: enriched.filePath,
    }
  }

  return { type: 'send_message', text: choice.message ?? choice.label }
}
